import { useId, type CSSProperties, type ReactNode } from "react";

export type SegmentedOption<T extends string | number> = {
  value: T;
  label: ReactNode;
  hint?: string;
};

/** A native radio group drawn as a pill track with a sliding thumb.
 * Arrow keys, form semantics and screen-reader grouping come from the radios. */
export function Segmented<T extends string | number>({
  name,
  label,
  value,
  options,
  onChange,
  disabled = false,
}: {
  name?: string;
  label: ReactNode;
  value: T;
  options: SegmentedOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
}) {
  const id = useId();
  const group = name ?? `seg-${id}`;
  const index = Math.max(
    0,
    options.findIndex((o) => String(o.value) === String(value)),
  );
  const hint = options[index]?.hint;
  const hasHints = options.some((o) => o.hint);
  return (
    <fieldset className="ui-field ui-segmented" disabled={disabled}>
      <legend className="ui-label">{label}</legend>
      <div
        className="segmented-track"
        style={
          { "--count": options.length, "--index": index } as CSSProperties
        }
      >
        <span className="segmented-thumb" aria-hidden="true" />
        {options.map((o) => (
          <label className="segmented-option" key={String(o.value)}>
            <input
              type="radio"
              className="ui-native"
              name={group}
              value={String(o.value)}
              checked={String(o.value) === String(value)}
              aria-describedby={hasHints ? `${id}-hint` : undefined}
              onChange={() => onChange(o.value)}
            />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
      {hasHints ? (
        <p className="ui-hint" id={`${id}-hint`}>
          {hint}
        </p>
      ) : null}
    </fieldset>
  );
}
